import {
  Article,
  BasicSearchResponse,
  DetailedSearch,
  FilterDocket,
  FilterSupplement, HeaderItemsRequest, ItemDetailRequest, PublicationType, PublicDocket, SearchFilters, SearchPublicationItem, SearchRequest,
  SearchSuggestion,
  SuggestionsResponse
} from '@shared/components/organisms/search-bar/models/search.model';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { SearchService } from '@shared/components/organisms/search-bar/services/search.service';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { ResponseStatus } from '@shared/models/base/response-status.model';
import { ClassificationContent } from '@shared/components/molecules/class-details/models/class-details.model';
import {
  ArticleChangeHistoryDetails,
  ArticleChangeHistoryDetailsContainer,
} from '@shared/components/molecules/article-details-history/models/article-change-history-details.model';
import { IResponse } from '@shared/models/base/http-response.model';
import { ItemDetailContent } from '@shared/components/molecules/item-details/models/item-details.model';
import { ErrorStore } from '@shared/stores/error.store';
import { UserStore } from '@shared/stores/user.store';
import { ArticleDetailsModalData } from '../../article-details-modal/models/article-details.model';

export interface SearchState {
  searchText: string;
  highlightPdfText: string;
  searchFilters: SearchFilters;
  suggestions: SearchSuggestion;
  detailSearch: DetailedSearch;
  searchResults: BasicSearchResponse;
  articleDetails: Article[];
  articleDetailModal: Article[];
  itemDetails: ItemDetailContent;
  articleChangeHistoryDetails: ArticleChangeHistoryDetails[];
  classificationDetails: ClassificationContent;
  supplementFilterOptions: FilterSupplement[];
  docketFilterOptions: PublicDocket[];
  suggestionsStatus: ResponseStatus;
  resultsStatus: ResponseStatus;
  articleDetailsStatus: ResponseStatus;
  articleDetailModalStatus: ResponseStatus;
  articleDetailsHistoryStatus: ResponseStatus;
  classificationDetailsStatus: ResponseStatus;
  itemDetailsStatus: ResponseStatus;
  itemModalStatus: ResponseStatus;
  detailSearchStatus: ResponseStatus;
  filtersStatus: ResponseStatus;
}

const initialState: SearchState = {
  searchText: '',
  highlightPdfText: '',
  searchFilters:
  {
    searchTerm: '',
    contentDate: undefined,
    supplementDateFrom: undefined,
    supplementDateTo: undefined,
    supplement: undefined,
    docket: undefined
  },
  suggestions: {
    hasMore: false,
    isNumber: false,
    suggestion: []
  },
  searchResults: {
    articles: [],
    searchHints: '',
    totalMatched: 0,
    searchedTerms: []
  },
  detailSearch: {
    articles: undefined,
    indexs: undefined,
    rules: undefined,
    packages: undefined,
    supplements: undefined,
    carriers: undefined,
    totalMatched: 0,
    searchedTerms: [],
    selectedListIndex: -1,
    displayMobileResultsPanel: false
  },
  articleDetails: [],
  articleDetailModal: [],
  itemDetails: {
    itemNumber: '',
    content: [],
    itemText: '',
    title: '',
    subTitle: '',
    // itemNumber: '',
    // groupingHeading: '',
    // grouping: '',
    // itemSubItems: [],
    // description: {
    //   descriptionText: ''
    // }
  },
  classificationDetails: {
    ruleText: '',
    title: '',
    content: '',
  },
  supplementFilterOptions: [],  // TODO get these values from the API
  docketFilterOptions: [],  // TODO get these values from the API
  articleChangeHistoryDetails: [],
  suggestionsStatus: ResponseStatus.IDLE,
  resultsStatus: ResponseStatus.IDLE,
  articleDetailsStatus: ResponseStatus.IDLE,
  articleDetailModalStatus: ResponseStatus.IDLE,
  articleDetailsHistoryStatus: ResponseStatus.IDLE,
  classificationDetailsStatus: ResponseStatus.IDLE,
  itemModalStatus: ResponseStatus.IDLE,
  detailSearchStatus: ResponseStatus.IDLE,
  itemDetailsStatus: ResponseStatus.IDLE,
  filtersStatus: ResponseStatus.IDLE,
};

export const SearchStore = signalStore(
  { providedIn: 'root' },
  withDevtools('search'),
  withState(initialState),
  withComputed((store, searchService = inject(SearchService), userStore = inject(UserStore)) => ({
    uiSuggestions: computed(() => store.suggestions()), // TODO - remove as this is only for dev purposes only
    uiResults: computed(() => {
      if (store.searchResults()?.articles) {
        return searchService.transformArticlesToResultRows(store.searchResults().articles, store.searchText(), true)
      }

      return [];
    }),
    uiItemResults: computed(() => searchService.addMarksToItemDetailContent(store.itemDetails(), store.searchText())),
    uiDetailResults: computed(() => searchService.transformDetailSearchToResultListTab(store.detailSearch(), store.searchText())),
    uiArticleDetails: computed(() => searchService.transformArticlesToResultRows(store.articleDetails(), '', false)),
    uiArticleDetailModal: computed(() => searchService.transformArticleDetailsToResultRows(store.articleDetailModal())),
    uiArticleDetail: computed(() => {
      return searchService.transformArticlesToResultRows(store.articleDetails(), store.searchText(), true)
    }),
    searchResultsCount: computed(() => store?.searchResults()?.articles?.length ?? 0),
    articleDetailsCount: computed(() => store?.articleDetails()?.length ?? 0),
    uiDocket: computed(() => store?.docketFilterOptions()),
    uiSupplement: computed(() => store?.supplementFilterOptions()),
    isModalSearching: computed(() => {
      return store.itemModalStatus() === ResponseStatus.LOADING
    }),
    isSearching: computed(() => {
      return store.resultsStatus() === ResponseStatus.LOADING
    }),
    isIdle: computed(() => {
      return store.resultsStatus() === ResponseStatus.IDLE
    }),
    isDetailSearching: computed(() => {
      return store.itemDetailsStatus() === ResponseStatus.LOADING
    }),
    isDetailIdle: computed(() => {
      return store.itemDetailsStatus() === ResponseStatus.IDLE
    })
  })),
  withMethods((store, searchService = inject(SearchService), errorStore = inject(ErrorStore)) => ({
    updateArticleFavoriteStatus(itemNumber: string, setIsFavorite: boolean) {
      const article = store.articleDetails().map(x => {
        x.isUpdateFavorite = true;
        if (x.itemNumber == itemNumber) {
          x.isFavorite = setIsFavorite;
          //return { ...x, isFavorite: setIsFavorite }
        }
        if (x.grouping?.headingNumber === itemNumber) {
          x.grouping.isFavorite = setIsFavorite;
          //return { ...x, grouping: { ...x.grouping, isFavorite: setIsFavorite } }
        }
        if (x.grouping?.subHeadingNumber === itemNumber) {
          x.grouping.isSubFavorite = setIsFavorite;
          //return { ...x, grouping: { ...x.grouping, isSubFavorite: setIsFavorite } }
        }
        return x;
      })
      patchState(store, {
        articleDetails: article
      });
    },
    clear() {
      patchState(store, {
        suggestions: {
          hasMore: false,
          isNumber: false,
          suggestion: []
        },
      });
    },
    clearArticleDetail() {
      patchState(
        store, {
        articleDetails: [],
        articleDetailsStatus: ResponseStatus.IDLE,
      })
    },

    clearSearchFilters() {
      patchState(
        store, {
        searchFilters: {
          searchTerm: '',
          contentDate: undefined,
          supplementDateFrom: undefined,
          supplementDateTo: undefined,
          supplement: undefined
        }
      })
    },
    clearSearch() {
      patchState(
        store, {
        searchResults: {
          articles: [],
          searchHints: '',
          totalMatched: 0,
          searchedTerms: []
        },

        articleDetailsStatus: ResponseStatus.IDLE,
      })
    },
    clearDetails() {
      patchState(
        store, {
        searchResults: {
          articles: [],
          searchHints: '',
          totalMatched: 0,
          searchedTerms: []
        },
        detailSearch: {
          articles: undefined,
          indexs: undefined,
          rules: undefined,
          packages: undefined,
          supplements: undefined,
          carriers: undefined,
          totalMatched: 0,
          searchedTerms: [],
          selectedListIndex: -1,
          displayMobileResultsPanel: false
        },
        itemDetails: {
          itemNumber: '',
          content: [],  
          itemText: '',
          title: '',
          subTitle: '',
        },
        articleDetails: [],
        resultsStatus: ResponseStatus.IDLE,
        detailSearchStatus: ResponseStatus.IDLE,
        articleDetailsStatus: ResponseStatus.IDLE,
      })
    },
    clearModalDetail() {
      patchState(
        store, {
        articleDetailModal: [],
        articleDetailModalStatus: ResponseStatus.IDLE
      })
    },
    setSearchFilters(search: SearchFilters) {
      patchState(store, { searchFilters: search });

    },
    setHighlightPdfText(text: string) {
      patchState(store, { highlightPdfText: text });
    },
    setDetailSearchSelectedIndex(index: number) {
      const newDetailSearch = { ...store.detailSearch(), selectedListIndex: index };
      updateState(store, 'search - selected list index', { detailSearch: newDetailSearch });
    },
    setDisplayMobileResultsPanel(display: boolean) {
      const newDetailSearch = { ...store.detailSearch(), displayMobileResultsPanel: display };
      updateState(store, 'search - display mobile results panel', { detailSearch: newDetailSearch });
    },
    loadSuggestions: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { suggestionsStatus: ResponseStatus.LOADING })),
        switchMap((searchText) =>
          searchService.getSearchSuggestions({ terms: searchText, type: "all", filters: {} }).pipe(
            tapResponse({
              next: (response: IResponse<SuggestionsResponse>) => {
                if (response?.success && response.data != null) {
                  patchState(store, {
                    suggestions: {
                      suggestion: response.data.suggestions,
                      hasMore: response.data.moreThanMax,
                      isNumber: response.data.isTermNumber,
                    },
                    suggestionsStatus: ResponseStatus.LOADED,
                  });
                }
              },
              error: (err) => {
                patchState(store, {
                  suggestions: {
                    hasMore: false,
                    isNumber: false,
                    suggestion: []
                  },
                  suggestionsStatus: ResponseStatus.ERROR,
                });
                console.error(err);
              },
            }),
          ),
        ),
      ),
    ),

    search: rxMethod<SearchRequest>(
      pipe(
        tap((searchRequest) => {
          patchState(store, { searchText: searchRequest.terms, resultsStatus: ResponseStatus.LOADING })
        }),
        switchMap((searchRequest) =>
          searchService.search(searchRequest).pipe(
            tapResponse({
              next: (response: IResponse<BasicSearchResponse>) => {
                if (response && response.data != null) {
                  patchState(store, {
                    searchResults: response.data
                  });
                  patchState(store, { articleDetails: response.data?.articles });

                }

                patchState(store, { resultsStatus: ResponseStatus.LOADED });

              },
              error: (err) => {
                patchState(store, {
                  searchResults: initialState.searchResults,
                  resultsStatus: ResponseStatus.ERROR
                });
                patchState(store, { articleDetails: initialState.articleDetails, resultsStatus: ResponseStatus.ERROR });
                console.error(err);
              },
            }),
          ),
        ),
      ),
    ),

    detailedSearch: rxMethod<SearchRequest>(
      pipe(
        tap((searchRequest) => patchState(store, { searchText: searchRequest.terms, detailSearchStatus: ResponseStatus.LOADING })),
        switchMap((searchRequest) =>
          searchService.detailedSearch(searchRequest).pipe(
            tapResponse({
              next: (resp: IResponse<DetailedSearch>) => {
                if (resp && resp.success) {
                  patchState(store, {
                    detailSearch: resp.data,
                    articleDetails: [],
                    detailSearchStatus: ResponseStatus.LOADED
                  });
                } else {
                  patchState(store, { detailSearch: initialState.detailSearch, detailSearchStatus: ResponseStatus.ERROR });
                  errorStore.logError({
                    success: false,
                    code: resp.code || '',
                    message: "SEARCH.ERRORS.RETRIEVING_DETAILS"
                  });
                }
              },
              error: (err) => {
                patchState(store, { detailSearch: initialState.detailSearch, detailSearchStatus: ResponseStatus.ERROR });
                console.error(err);
              },
            }),
          ),
        ),
      ),
    ),
    getArticleDetails: rxMethod<string>(
      pipe(
        tap((articleId: string) => patchState(store, { searchText: articleId, articleDetailsStatus: ResponseStatus.LOADING, itemDetailsStatus: ResponseStatus.LOADING, resultsStatus: ResponseStatus.LOADING })),
        switchMap((articleId: string) => {
          const date = store.searchFilters().contentDate ?? undefined;
          return searchService.getArticleDetails({ itemNumber: articleId, type: 'Article', contentDate: date }).pipe(
            tapResponse({
              next: (resp: IResponse<Article[]>) => {
                patchState(store, { articleDetails: resp.data, articleDetailsStatus: ResponseStatus.LOADED, itemDetailsStatus: ResponseStatus.LOADED, resultsStatus: ResponseStatus.LOADED });
              },
              error: (err) => {

                patchState(store, { articleDetails: initialState.articleDetails, articleDetailsStatus: ResponseStatus.ERROR, itemDetailsStatus: ResponseStatus.ERROR, resultsStatus: ResponseStatus.ERROR });
                console.error(err);
              },
            }),
          )
        }),
      ),
    ),
    getArticleMoreDetails: rxMethod<HeaderItemsRequest>(
      pipe(
        tap(() => patchState(store, { articleDetailsStatus: ResponseStatus.LOADING })),
        switchMap((request: HeaderItemsRequest) => {
          request.contentDate = store.searchFilters().contentDate ?? undefined;
          return searchService.getArticleMoreDetails(request).pipe(
            tapResponse({
              next: (response: IResponse<Article[]>) => {

                let articles: Article[] = store.articleDetails();
                const index = articles.findIndex(article => article.itemNumber === request.itemNumber);
                // If index is -1, then we are loading more before a Generic heading
                // Just append or prepend the data
                if (index == -1) {
                  if (response.data) {
                    if (request.isAbove)
                      articles = response.data.concat(articles);

                    else
                      articles = articles.concat(response.data);
                  }
                }
                else {
                  if (request.isAbove)
                    articles[index].headingDataAbove = false;
                  else
                    articles[index].headingDataBelow = false;



                  if (response.data) {
                    // Add response.data to the end of articles array, remove duplicates, and sort by itemNumber
                    articles = articles.concat(response.data);

                    // Remove duplicates by itemNumber
                    const uniqueArticles = new Map<string, Article>();
                    articles.forEach(article => {
                      if (!uniqueArticles.has(article.itemNumber)) {
                        uniqueArticles.set(article.itemNumber, article);
                      }
                    });
                    articles = Array.from(uniqueArticles.values());

                    // Sort by itemNumber
                    articles.sort((a, b) => {
                      const numA = parseFloat(a.itemNumber) || 0;
                      const numB = parseFloat(b.itemNumber) || 0;
                      return numA - numB;
                    });
                  }


                  // if (request.isAbove)
                  //   articles = response.data.concat(articles);

                  // else
                  //   articles = articles.concat(response.data);

                  // Check if an Item is in the middle of the array
                  // // Than determine if the headingDataAbove and headingDataBelow should be set to false
                  // index = articles.findIndex(article => article.itemNumber === request.itemNumber);
                  // if (!(index == 0 || index == articles.length - 1)) {
                  //   // Look for a matching group above and below.
                  //   // Than we know the item is in the middle and no more data can be loaded
                  //   if (articles[index].groupingItemNumber === articles[index - 1].groupingItemNumber) {
                  //     articles[index].headingDataAbove = false;
                  //   }
                  //   if (articles[index].groupingItemNumber === articles[index + 1].groupingItemNumber) {
                  //     articles[index].headingDataBelow = false;
                  //   }
                  // }
                }
                patchState(store, {
                  articleDetails: articles, articleDetailsStatus: ResponseStatus.LOADED
                });

              },
              error: (err) => {
                patchState(store, { articleDetails: initialState.articleDetails, articleDetailsStatus: ResponseStatus.ERROR });
                console.error(err);
              },
            }),
          );
        }),
      ),
    ),
    getArticleDetailModal: rxMethod<ArticleDetailsModalData>(
      pipe(
        tap(() => patchState(store, { articleDetailModalStatus: ResponseStatus.LOADING })),
        switchMap((articleDetailsModalData: ArticleDetailsModalData) =>
          searchService.getArticleModalDetails({ itemNumber: articleDetailsModalData.articleId, type: 'Article', contentDate: articleDetailsModalData.articleDate }).pipe(
            tapResponse({
              next: (resp: IResponse<Article[]>) => {
                // Changed the response to always pass back an array
                if (resp.data && resp.data.length > 0)
                  patchState(store, { articleDetailModal: resp.data, articleDetailModalStatus: ResponseStatus.LOADED });
              },
              error: (err) => {
                patchState(store, { articleDetailModal: initialState.articleDetailModal, articleDetailModalStatus: ResponseStatus.ERROR });
                console.error(err);
              },
            }),
          ),
        ),
      ),
    ),
    getClassificationDetails: rxMethod<number>(
      pipe(
        tap(() => patchState(store, { classificationDetailsStatus: ResponseStatus.LOADING })),
        switchMap((classId: number) =>
          searchService.getClassificationDetails(classId).pipe(
            tapResponse({
              next: (resp: IResponse<ClassificationContent>) => {
                patchState(store, {
                  classificationDetails: resp.data,
                  classificationDetailsStatus: ResponseStatus.LOADED,
                });
              },
              error: (err) => {
                patchState(store, {
                  classificationDetails: initialState.classificationDetails,
                  articleDetailsStatus: ResponseStatus.ERROR,
                  resultsStatus: ResponseStatus.ERROR,
                });
                console.error(err);
              },
            }),
          ),
        ),
      ),
    ),
    getItemDetails: rxMethod<{ request: ItemDetailRequest; fromModal?: boolean }>(
      pipe(
        tap(({ fromModal }) => {
          patchState(store, {
            ...(fromModal ? { itemModalStatus: ResponseStatus.LOADING } : { itemDetailsStatus: ResponseStatus.LOADING })
          });
        }),
        switchMap(({ request, fromModal }) => {
          request.contentDate = store.searchFilters().contentDate ?? undefined;
          return searchService.getItemDetails(request).pipe(
            tapResponse({
              next: (response: IResponse<ItemDetailContent>) => {
                if (response && response.data != null) {
                  if (response.success) {
                    patchState(store, {
                      itemDetails: response.data,
                      ...(fromModal ? { itemModalStatus: ResponseStatus.LOADED } : { itemDetailsStatus: ResponseStatus.LOADED })
                    });
                  }
                } else {
                  errorStore.logError({
                    success: false,
                    code: response.code || '',
                    message: "SEARCH.ERRORS.RETRIEVING_DETAILS"
                  });
                }
              },
              error: () => {
                patchState(store, {
                  itemDetails: initialState.itemDetails,
                  ...(fromModal ? { itemModalStatus: ResponseStatus.ERROR } : { itemDetailsStatus: ResponseStatus.ERROR })
                });
                errorStore.logError({
                  success: false,
                  code: '',
                  message: "SEARCH.ERRORS.RETRIEVING_DETAILS"
                });
              },
            }),
          );
        })
      )
    ),
    getItemDetailsHistory: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { itemModalStatus: ResponseStatus.LOADING })),
        switchMap((articleId: string) =>
          searchService.getArticleDetailsHistory(articleId).pipe(
            tapResponse({
              next: (resp: IResponse<ArticleChangeHistoryDetailsContainer>) => {
                patchState(store, {
                  articleChangeHistoryDetails: resp.data?.articleChangeHistoryDetails,
                  itemModalStatus: ResponseStatus.LOADED,
                });
              },
              error: (err) => {
                patchState(store, {
                  articleChangeHistoryDetails: [],
                  itemModalStatus: ResponseStatus.ERROR,
                });
                console.error(err);
              },
            }),
          ),
        ),
      ),
    ),
    loadFilterOptions: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { filtersStatus: ResponseStatus.LOADING })),
        switchMap(() =>
          searchService.getPublicationDropDownList().pipe(
            tapResponse({
              next: (resp: IResponse<SearchPublicationItem[]>) => {
                if (resp.data && resp.success) {
                  const Dockets: FilterDocket[] = resp.data
                    .filter(item => item.type === PublicationType.Docket)
                    .map(item => ({ id: item.id, text: item.name }));
                  const supplements: FilterSupplement[] = resp.data
                    .filter(item => item.type === PublicationType.Supplement)
                    .map(item => ({ id: item.id, text: item.name }));

                  patchState(store, {

                    docketFilterOptions: Dockets,
                    supplementFilterOptions: supplements,
                    filtersStatus: ResponseStatus.LOADED,

                  });
                }
              },
              error: (err) => {
                patchState(store, {
                  articleChangeHistoryDetails: [],
                  articleDetailsStatus: ResponseStatus.ERROR,
                });
                console.error(err);
              }
            })
          )
        )
      )
    )
  })),
  withHooks({
    onInit(store) {
      patchState(store, {
        searchResults: {
          articles: [],
          searchHints: '',
          totalMatched: 0,
          searchedTerms: []
        },
        searchText: '',
      });
      //store.loadSuggestions();
    },
    onDestroy(store) {
      patchState(store, {
        searchResults: {
          articles: [],
          searchHints: '',
          totalMatched: 0,
          searchedTerms: []
        },
        searchText: '',
      });
    },
  }),
);
